import { IQueryTableResult } from "#services/pilotService/responses/types";
import {
    Interpretation,
    OperatorInvocation,
} from "#services/pilotService/types";
import {
    selectAllOperatorInvocationsByActionId,
    selectId,
    selectInterpretationById,
    selectInterpretationsThatAreFavorited,
} from "#selectors";
import { ApplicationState, IoState, isDone } from "#store/types";
import { isPresent, Optional } from "#types";
import { createSelector } from "reselect";
import { TableResultMap } from "#store/tables";

export type TableResultView = {
    active: boolean;
    title: string;
    explanation: string;
    id: number;
    table: IQueryTableResult;
    // score: number;
};

export type MappedTable = {
    interpretationIdx: number;
    table: IQueryTableResult;
};

export const selectTablesMap: (state: ApplicationState) => TableResultMap = (
    state: ApplicationState
) => {
    return state.tables.tableResults;
};

const selectTableResultsRaw = createSelector(
    [selectTablesMap],
    (map): IoState<IQueryTableResult>[] => {
        return Object.values(map).filter(isPresent);
    }
);

export const selectTablesLoaded = createSelector(
    [selectTableResultsRaw],
    (state: IoState<IQueryTableResult>[]): IQueryTableResult[] => {
        return state.filter(isDone).map(x => x.payload);
    }
);

export const selectTablesIfFavoritedInterpretationId = createSelector(
    [selectInterpretationsThatAreFavorited, selectTablesLoaded],
    (interpretations, tables) => {
        return tables.filter(x =>
            interpretations.map(y => y.table).includes(x.tableId)
        );
    }
);

export const selectFavoritedTableResults = (
    state: ApplicationState
): IQueryTableResult[] => {
    return selectInterpretationsThatAreFavorited(state)
        .map(interpretation => interpretation.table)
        .filter(isPresent)
        .map(tid => state.tables.tableResults[tid])
        .filter(isPresent)
        .filter(isDone)
        .map(x => x.payload);
};

// export const selectFavoritedTableResultsView = (
//     state: ApplicationState
// ): TableResultView[] => {
function getInterpretationTable(
    interpretation: Interpretation,
    active: boolean,
    state: ApplicationState
): TableResultView | undefined {
    const tableId = interpretation.table;
    if (tableId !== undefined) {
        const table = state.tables.tableResults[tableId];
        if (isPresent(table) && isDone(table)) {
            const t = table.payload;
            return {
                active: active,
                title: t.sourceQuery,
                explanation:
                    (t.meta.explanation as string) ||
                    "No sql2nl explanation available",
                id: interpretation.id,
                table: t,
            };
        }
    }
    return undefined;
}

export const selectFavoritedTableResultsView = (
    state: ApplicationState
): TableResultView[] => {
    const activeInterpretations = selectInterpretationsThatAreFavorited(state);

    return activeInterpretations
        .map(x => getInterpretationTable(x, true, state))
        .filter(isPresent)
        .sort((x, y) => {
            return x.active === y.active ? 0 : x.active ? -1 : 1;
        });
};

export const selectTableResultRawById = createSelector(
    [selectTablesMap, selectId],
    (tables, tableId): Optional<IoState<IQueryTableResult>> => {
        return tables[tableId];
        //}, { memoizeOptions: {maxSize:100}
    }
);

export const selectTableResult = createSelector(
    [selectTableResultRawById],
    (r): null | IQueryTableResult => {
        if (!r || r.state !== "done") {
            return null;
        }
        return r.payload;
    }
);

export const selectTableResultsByActionId = (
    state: ApplicationState,
    actionId: number
): IoState<IQueryTableResult>[] => {
    const ops: OperatorInvocation[] = selectAllOperatorInvocationsByActionId(
        state,
        actionId
    );
    const interpretationIds: number[] = ops.flatMap(
        (op: OperatorInvocation) => op.interpretations
    );
    const tableIds: number[] = interpretationIds
        .map(iid => selectInterpretationById(state, iid))
        .filter(isPresent)
        .map(i => i.table);

    return tableIds
        .map(tid => {
            return selectTableResultRawById(state, tid);
        })
        .filter(isPresent);
};

export const selectTableResults = (
    state: ApplicationState,
    tableIds: number[]
): Optional<IQueryTableResult>[] => {
    return tableIds.map(x => {
        if (
            state.tables.tableResults[x] === undefined ||
            state.tables.tableResults[x].state !== "done"
        ) {
            return null;
        } else {
            return state.tables.tableResults[x].payload;
        }
    });
};

export const selectTableByInterpretationId = createSelector(
    [selectInterpretationById, selectTablesLoaded],
    (interpretation, tables) => {
        if (isPresent(interpretation)) {
            const tableId = interpretation.table;
            if (isPresent(tableId)) {
                return tables.find(x => x.tableId === tableId);
            }
        }
        return undefined;
    }
);

export const selectTableResultsByIds = createSelector(
    [selectTablesMap, (state: ApplicationState, ids: number[]) => ids],
    (tables, ids): IoState<IQueryTableResult>[] => {
        return ids.map(id => tables[id]).filter(isPresent);
    }
);

const selectAllInterpretationTables = (
    state: ApplicationState,
    interpretations: Interpretation[]
): Optional<IQueryTableResult>[] => {
    const tableIds = interpretations.map(x => x.table);
    return selectTableResults(state, tableIds);
};

export const selectMappedInterpretationTables = createSelector(
    [selectAllInterpretationTables],
    (allTables: Optional<IQueryTableResult>[]): MappedTable[] => {
        const interpretationMap = Array.from(
            Array(allTables.length).keys()
        ).filter(x => isPresent(allTables[x]));
        return allTables.filter(isPresent).map((d, i) => {
            return {
                interpretationIdx: interpretationMap[i],
                table: d,
            };
        });
    }
);
